import React, { useState, useEffect, useMemo, useImperativeHandle, useRef } from 'react';
import { connect } from 'react-redux';
import { i18n } from 'fishx';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import concat from 'lodash/concat';

import { Form } from '@whalecloud/fdx';

import { renderFormItems, getFormErrorI18n } from '@/utils';

import SubscriberAttributes from './SubscriberAttributes';
import ComboUnitOffer from './ComboUnitOffer';
import OfferAttributes from './OfferAttributes';
import SharedDeviceAttr from './SharedDeviceAttr';
import AddressAttributes from './AddressAttributes';

import { getExchangeOfferFormItems, needQueryAttrs } from './utils';

const AttributeInfo = props => {
  const {
    modelKey, refInstance, currentNumber = {}, addressId, queryNumberParams, queryExchangeOfferParams, onNumberChange, renderFun = {},
    isCombo, isCasual, isPromotion, offerDetail, attrObj, needSharedDevice, exchangeId, stdCode,
    dispatch, form, queryOfferParams, subsId, mainOfferCurrentDpp,
  } = props;

  const { getFieldDecorator, getFieldsValue, validateFields } = form;
  const [exchangeOfferList, setEchangeOfferList] = useState([]); // 临时套餐列表

  const comboUnitRef = useRef();
  const subsAttrRef = useRef();
  const offerGroupRef = useRef();
  const sharedDeviceRef = useRef();
  const addressAttrRef = useRef();

  useImperativeHandle(refInstance, () => ({
    getAttrObj: async () => {
      await validateFields();
      let subsAttrs = {};
      let addressAttrbutes;
      if (addressAttrRef.current) {
        // 获取号码属性相关属性信息。
        addressAttrbutes = await addressAttrRef.current.getAddressAttrbutes(); // 订户属性
      }
      if (subsAttrRef.current) {
        // 获取临时套餐相关属性信息。
        const exchangedInfo = await getFieldsValue(['BSNL_CASUAL_PLAN_CHANGE_PLAN', 'BSNL_CASUAL_PLAN_EXP_OFF']);
        subsAttrs = await subsAttrRef.current.getAttributeInfo(exchangedInfo || {}); // 订户属性
      }
      if (!isEmpty(addressAttrbutes)) {
        if (subsAttrs && !isEmpty(subsAttrs.subsAttrList)) {
          subsAttrs.subsAttrList = concat(subsAttrs.subsAttrList, addressAttrbutes);
        } else {
          subsAttrs.subsAttrList = addressAttrbutes;
        }
      }
      let comboUnitList = {};
      if (comboUnitRef.current) {
        comboUnitList = await comboUnitRef.current.getComboUnitInfo(); // combo数据
      }
      let offerGroupInfo = {};
      if (!isCombo && offerGroupRef.current) {
        offerGroupInfo = await offerGroupRef.current.getOfferGroupInfo();
      }
      if (sharedDeviceRef.current) {
        const sharedDeviceAttr = await sharedDeviceRef.current.getSharedDeviceAttributeInfo();
        return {
          subsAttrs,
          comboUnitList,
          offerGroupInfo,
          sharedGoodsAttrDtoList: sharedDeviceAttr,
        };
      }
      return {
        subsAttrs,
        comboUnitList,
        offerGroupInfo,
      };
    },
  }));

  useEffect(() => {
    dispatch({
      type: 'business/qryAttrInfoBsnl', // 查询属性信息
      payload: {
        attrCodes: needQueryAttrs.join(),
      },
      modelName: modelKey,
    });
  }, []);

  useEffect(() => {
    if (isPromotion || isCasual) { // 如果是临时套餐，需要查询临时套餐可切换套餐列表
      dispatch({
        type: 'selectPlan/qryExchangeablePlanBsnl',
        payload: { addressId, ...queryExchangeOfferParams },
      }).then(result => {
        setEchangeOfferList(result || []);
      });
    }
  }, [isPromotion, isCasual]);

  // 临时套餐相关 ---------
  const exchangeOfferFormItems = useMemo(() => getExchangeOfferFormItems({
    isCasual,
    isPromotion,
    validPeriodList: get(attrObj, 'BSNL_CASUAL_PLAN_EXP_OFF.valueList') || [],
    exchangeOfferList,
  }), [attrObj]);


  // 号码选择 ------
  const numberFormItems = useMemo(() => ([
    {
      label: i18n('SELECT_NUMBER'),
      name: 'number',
      options: {
        initialValue: !isEmpty(currentNumber) ? currentNumber : null,
        rules: [{ required: true, message: getFormErrorI18n(i18n('SELECT_NUMBER')) }],
      },
      type: 'numberSelect',
      formItemCompProps: {
        addressId,
        exchangeId,
        stdCode,
        ...queryNumberParams,
        onChange: number => onNumberChange(number),
      },
    },
  ]), [stdCode]);

  const renderNumberFormItem = () => {
    if (renderFun.renderOldNbr) {
      return renderFun.renderOldNbr();
    }
    return (
      <div className="blockWrap">
        <h3 className="blockTitle">{i18n('STEP_SERVICE_NUMBER_SELECTION')}</h3>
        {['shift', 'transfer', 'reconnection'].includes(modelKey) && <span className="note">{i18n('NUMBER_SELECTION_NOTE')}</span>}
        <div className="formItemBox">{renderFormItems(numberFormItems, getFieldDecorator)}</div>
      </div>
    );
  };

  const renderSharedDevice = () => { // 承载套餐属性采集
    const servType = `${get(offerDetail, 'indepProdSpecDto.servType') || ''}`;
    if (!isCombo && needSharedDevice && servType !== '26' && modelKey === 'newConnection') {
      return (
        <SharedDeviceAttr ref={sharedDeviceRef} modelKey={modelKey} />
      );
    }
    return '';
  };

  return (
    <div className="formBlock">
      {
        (['shift', 'newConnection'].includes(modelKey)) && (
          <AddressAttributes
            ref={addressAttrRef}
            modelKey={modelKey}
            addressId={addressId}
            disabled={modelKey === 'newConnection' ? (renderFun.renderOldNbr || renderFun.renderComboInfo) : false}
          />
        )
      }
      <Form layout="inline">
        {
          (!isCombo && modelKey !== 'modifyPlan') && renderNumberFormItem()
        }
        { // 临时套餐可切换套餐和临时套餐有效期采集
          (isPromotion || isCasual) && (
            <div className="blockWrap">
              <h3 className="blockTitle">{i18n('CASUAL_PLAN_EXPIRE_CHANGE_INFO')}</h3>
              <div className="formItemBox">{renderFormItems(exchangeOfferFormItems, getFieldDecorator)}</div>
            </div>
          )
        }
      </Form>
      {
        renderFun.renderComboInfo && renderFun.renderComboInfo()
      }
      {
        (isCombo && !renderFun.renderComboInfo) && (
          <ComboUnitOffer
            ref={comboUnitRef}
            blockName="comboUnit"
            modelKey={modelKey}
            offerDetail={offerDetail}
            addressId={addressId}
            exchangeId={exchangeId}
            stdCode={stdCode}
            mainOfferCurrentDpp={mainOfferCurrentDpp}
            qrySalesRuleParams={{ ...queryOfferParams, subsId }}
          />
        )
      }
      {
        (!isCombo && !renderFun.renderOldOffer) && (
          <OfferAttributes ref={offerGroupRef} offerDetail={offerDetail} modelKey={modelKey} needSharedDevice={needSharedDevice} />
        )
      }
      { // 承载套餐属性
        renderSharedDevice()
      }
      {
        (!isCombo && !renderFun.renderOldOffer) && (
          <SubscriberAttributes
            ref={subsAttrRef}
            modelKey={modelKey}
            addressId={addressId}
            offerDetail={offerDetail}
          />
        )
      }
    </div>
  );
};

const Component = connect((state, { modelKey }) => ({
  loading: state.loading.effects['selectPlan/qryAttrInfoBsnl'],
  isCombo: state[modelKey].isCombo,
  isCasual: state[modelKey].isCasual,
  isPromotion: state[modelKey].isPromotion,
  offerDetail: state[modelKey].offerDetail,
  attrObj: state[modelKey].attrObj || {},
  needSharedDevice: state[modelKey].needSharedDevice,
  exchangeId: state[modelKey].exchangeId || null,
  stdCode: state[modelKey].stdCode || null,
}))(Form.create()(AttributeInfo));
export default React.forwardRef((props, ref) => <Component {...props} refInstance={ref} />);
